import { useId, useMemo, type ReactElement, type ReactNode } from 'react';
import { PiFilterChips, PiToolbar } from '../pi-kit';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { Select } from '@/components/ui/Select';
import type { LaeProdCycleOption, LaeProdFunnelOption, LaeProdSource } from '@/lib/types';
import { todayIst } from './lae-dates';
import { resolveDefaultCycle, type LaeProductivityParams } from './lae-params';
import { PeriodControl } from './PeriodControl';

/*
 * LaeControlsBar: the controls of Product Insights · LAE (Productivity, Schools mode).
 *
 * Ports, from SEED Apps lae-app features/productivity:
 *   - the two controls rows of ProductivityPage.jsx (row 1: mode switch left; Source switch, period
 *     control and layout toolbar right. Row 2: the FilterBar);
 *   - FilterBar.jsx in Schools mode (Funnel, then Cycle; School and Region are Counsellors mode only);
 *   - SourceSwitch.jsx (All calls / System / Manual).
 *
 * Deliberate differences from LAE:
 *   - No ModeSwitch (Schools mode only), so row 1 holds only its right-hand group.
 *   - `actions` is the slot the page fills with LayoutToolbar and UpdatedFact, at the place LAE puts
 *     its LayoutToolbar (the end of row 1, after the period control).
 *   - Not sticky: other Product Insights toolbars are not pinned, and the Schools table pins its own header.
 *   - Every LAE tooltip (source options, Funnel and Cycle labels) is dropped: they are definitions, which the
 *     no-helper-text rule keeps out of the UI. The visible labels Funnel and Cycle stay; they are names.
 *   - The default cycle is the CURRENT cycle (P4 resolveDefaultCycle), not LAE's constant 2026-27; it is
 *     prepended when the server omits it and marked "(current)".
 *   - A chips row (PiFilterChips) lists the non-default Cycle, Funnel and Calls values, with Reset. The
 *     period is never a chip: the control and the page's facts line always show it.
 *
 * Cycle state from P4: `cycle` is the cycle in force ('' = All cycles), `defaultCycle` the current one and
 * `cycleIsDefault` decides the chip.
 */

type LaeParams = LaeProductivityParams;
type CycleInput = LaeProdCycleOption | string;
type FunnelInput = LaeProdFunnelOption | { value: string; label?: string } | string;

const DEFAULT_FUNNEL = 'prospect_to_application';
const DEFAULT_FUNNEL_LABEL = 'Prospect to application';

const SOURCE_OPTIONS: readonly { value: LaeProdSource; label: string }[] = [
  { value: 'all', label: 'All calls' },
  { value: 'system', label: 'System' },
  { value: 'manual', label: 'Manual' },
];

const SOURCE_CHIP: Partial<Record<LaeProdSource, string>> = {
  system: 'System calls',
  manual: 'Manual calls',
};

/** Spellings differ by spaces upstream ("2026 - 27" = "2026-27"). */
const norm = (s: string | null | undefined): string => String(s ?? '').replace(/\s+/g, '');

function cycleValueOf(c: CycleInput | null | undefined): string {
  if (c == null) return '';
  return typeof c === 'string' ? c : String(c.value ?? '');
}

function funnelOptionOf(f: FunnelInput | null | undefined): { value: string; label: string } | null {
  if (f == null) return null;
  if (typeof f === 'string') return f ? { value: f, label: f } : null;
  const value = String(f.value ?? '');
  if (!value) return null;
  return { value, label: f.label || value };
}

function Labelled({ label, htmlFor, children }: { label: string; htmlFor: string; children: ReactNode }): ReactElement {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <label htmlFor={htmlFor} className="shrink-0 text-xs font-medium tracking-wide text-fg-subtle uppercase">
        {label}
      </label>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

export function LaeControlsBar({
  params,
  cycles,
  funnels,
  loading,
  actions,
}: {
  params: LaeParams;
  cycles: readonly CycleInput[] | null | undefined;
  funnels: readonly FunnelInput[] | null | undefined;
  loading: boolean;
  actions?: ReactNode;
}): ReactElement {
  const baseId = useId();
  const funnelId = `${baseId}-funnel`;
  const cycleId = `${baseId}-cycle`;

  const funnel = params.funnel;
  const source = params.source;

  const cycleValues = useMemo(
    () => (cycles ?? []).map(cycleValueOf).filter((v) => v !== ''),
    [cycles],
  );

  // The current cycle (P4 resolveDefaultCycle; null = no cycles listed). P4 already resolves it from
  // the cycles the page gave it; the local call only covers a bar that is handed a newer list.
  const currentCycle = useMemo<string | null>(
    () => params.defaultCycle ?? (cycles && cycles.length > 0 ? resolveDefaultCycle(cycles, params.today || todayIst()) : null),
    [params.defaultCycle, params.today, cycles],
  );

  /** The cycle in force: '' = All cycles. */
  const effectiveCycle = params.cycle;
  const isCurrentCycle = (v: string): boolean => currentCycle != null && norm(v) === norm(currentCycle);

  const cycleOptions = useMemo(() => {
    const values = [...cycleValues];
    if (currentCycle && !values.some((v) => norm(v) === norm(currentCycle))) values.unshift(currentCycle);
    // Keep a URL cycle the server did not list, so the Select shows what is in force.
    if (effectiveCycle && !values.some((v) => norm(v) === norm(effectiveCycle))) values.push(effectiveCycle);
    return values;
  }, [cycleValues, currentCycle, effectiveCycle]);

  const cycleSelectValue = effectiveCycle
    ? (cycleOptions.find((v) => norm(v) === norm(effectiveCycle)) ?? effectiveCycle)
    : '';

  const funnelOptions = useMemo(() => {
    const list: { value: string; label: string }[] = [];
    for (const f of funnels ?? []) {
      const o = funnelOptionOf(f);
      if (o && !list.some((x) => x.value === o.value)) list.push(o);
    }
    if (!list.some((f) => f.value === DEFAULT_FUNNEL)) list.unshift({ value: DEFAULT_FUNNEL, label: DEFAULT_FUNNEL_LABEL });
    if (funnel && !list.some((f) => f.value === funnel)) list.push({ value: funnel, label: funnel });
    return list;
  }, [funnels, funnel]);

  const funnelLabel = funnelOptions.find((f) => f.value === funnel)?.label ?? funnel;

  const chips: { key: string; label: ReactNode; onRemove: () => void }[] = [];
  if (!params.cycleIsDefault) {
    chips.push({
      key: 'cycle',
      label: effectiveCycle === '' ? 'All cycles' : cycleSelectValue,
      onRemove: () => params.setCycle(currentCycle ?? ''),
    });
  }
  if (funnel) {
    chips.push({ key: 'funnel', label: funnelLabel, onRemove: () => params.setFunnel('') });
  }
  const sourceChip = SOURCE_CHIP[source];
  if (sourceChip) {
    chips.push({ key: 'source', label: sourceChip, onRemove: () => params.setSource('all') });
  }

  return (
    <PiToolbar chips={chips.length > 0 ? <PiFilterChips chips={chips} onReset={params.resetFilters} /> : null}>
      <div className="flex w-full min-w-0 flex-col gap-2">
        {/* Row 1: LAE puts its mode switch on the left; Schools mode only, so the right-hand group alone. */}
        <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
          <SegmentedControl
            label="Calls"
            size="sm"
            value={source}
            options={SOURCE_OPTIONS}
            onChange={(v) => params.setSource(v)}
            className="w-auto max-w-full"
          />
          <PeriodControl
            preset={params.preset}
            day={params.day}
            range={params.range}
            onChange={params.setPeriod}
            onStep={params.stepDay}
          />
          {actions}
        </div>

        {/* Row 2: LAE FilterBar, Schools mode. */}
        <div className="grid min-w-0 grid-cols-1 items-center gap-x-5 gap-y-2 sm:grid-cols-2 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <Labelled label="Funnel" htmlFor={funnelId}>
            <Select
              id={funnelId}
              value={funnel}
              disabled={loading}
              onChange={(e) => params.setFunnel(e.target.value)}
            >
              <option value="">All funnels</option>
              {funnelOptions.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </Select>
          </Labelled>

          <Labelled label="Cycle" htmlFor={cycleId}>
            <Select
              id={cycleId}
              value={cycleSelectValue}
              disabled={loading}
              onChange={(e) => params.setCycle(e.target.value)}
            >
              <option value="">All cycles</option>
              {cycleOptions.map((v) => (
                <option key={v} value={v}>
                  {isCurrentCycle(v) ? `${v} (current)` : v}
                </option>
              ))}
            </Select>
          </Labelled>
        </div>
      </div>
    </PiToolbar>
  );
}
